import React from "react";
import PropTypes from "prop-types";

export default function RightBodyPane({
  styles,
  rightBodyRef,
  rightPaneWidth,
  height,
  ROW_HEIGHT,
  totalRow,
  rows,
  scrollableGroups,
  getGroupObj,
  getChildVal,
  formatValue,
}) {
  const getRowKey = (row, index) =>
    // Try to find a unique identifier in order of preference
    row?.id ??
    row?.Id ??
    // Fallback to combining multiple fields for uniqueness
    (row?.["Basepack Desc"]
      ? `${row.Basepack}_${row["Basepack Desc"]}_${index}`
      : undefined) ??
    row?.Basepack ??
    row?.BG ??
    row?.Format ??
    // Last resort: use stringified object with index (includes all data)
    `row_${index}_${JSON.stringify(row)}`;

  return (
    <div
      ref={rightBodyRef}
      className={styles.hideScrollbar}
      style={{
        overflowX: "auto",
        overflowY: "auto",
        WebkitOverflowScrolling: "touch",
        background: "#FFFFFF",
        boxSizing: "border-box",
        width: rightPaneWidth,
        minWidth: rightPaneWidth,
        height: typeof height === "number" ? `${height}px` : height,
      }}
    >
      {totalRow && (
        <div className={styles.scrollableGroups}>
          {scrollableGroups.map((grp) => {
            const groupWidth =
              (grp.children?.length || 0) * (grp.widthPerCol || 100);
            const groupObj = getGroupObj(totalRow, grp.group) || {};
            return (
              <div
                key={`total-right-group-${grp.group}`}
                className={styles.totalGroupWrapper}
                style={{
                  flex: `0 0 ${groupWidth}px`,
                  minWidth: `${groupWidth}px`,
                  maxWidth: `${groupWidth}px`,
                }}
              >
                {grp.children.map((childLabel, cIndex) => {
                  const rawVal = getChildVal(groupObj, childLabel);
                  const actualValue = rawVal?.value ?? rawVal;
                  const val = formatValue(actualValue, {
                    columnLabel: childLabel,
                    isTotalRow: true,
                  });

                  const isNumber = typeof rawVal === "number";

                  return (
                    <div
                      key={`total-right-cell-${grp.group}-${childLabel}`}
                      className={styles.totalCell}
                      style={{
                        height: ROW_HEIGHT,
                        lineHeight: `${ROW_HEIGHT}px`,
                        flex: `0 0 ${grp.widthPerCol}px`,
                        minWidth: `${grp.widthPerCol}px`,
                        maxWidth: `${grp.widthPerCol}px`,
                        borderRight:
                          cIndex === grp.children.length - 1
                            ? "none"
                            : "1px solid #E6ECFA",
                        textAlign: isNumber ? "right" : "left",
                      }}
                      title={
                        typeof rawVal?.value === "number"
                          ? rawVal.value.toLocaleString("en-IN")
                          : (rawVal?.value ?? rawVal ?? "-")
                      }
                    >
                      {val}
                      {rawVal?.isPercentage === "Yes" && "%"}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}

      {rows.map((row, index) => (
        <div
          key={`body-row-right-${getRowKey(row, index)}`}
          className={styles.bodyRowRight}
        >
          {scrollableGroups.map((grp) => {
            const groupWidth =
              (grp.children?.length || 0) * (grp.widthPerCol || 100);
            const groupObj = getGroupObj(row, grp.group) || {};

            return (
              <div
                key={`body-group-${getRowKey(row, index)}-${grp.group}`}
                className={styles.bodyGroupWrapper}
                style={{
                  flex: `0 0 ${groupWidth}px`,
                  minWidth: `${groupWidth}px`,
                  maxWidth: `${groupWidth}px`,
                }}
              >
                {grp.children.map((childLabel, cIndex) => {
                  const rawVal = getChildVal(groupObj, childLabel);
                  const actualValue = rawVal?.value ?? rawVal;
                  const val = formatValue(actualValue);
                  const isNumber = typeof actualValue === "number";

                  return (
                    <div
                      key={`body-cell-${getRowKey(row, index)}-${grp.group}-${childLabel}`}
                      className={styles.bodyCell}
                      style={{
                        height: ROW_HEIGHT,
                        lineHeight: `${ROW_HEIGHT}px`,
                        flex: `0 0 ${grp.widthPerCol}px`,
                        minWidth: `${grp.widthPerCol}px`,
                        maxWidth: `${grp.widthPerCol}px`,
                        borderRight:
                          cIndex === grp.children.length - 1
                            ? "none"
                            : "1px solid #F3F4F8",
                        textAlign: isNumber ? "right" : "left",
                      }}
                      title={
                        typeof actualValue === "number"
                          ? actualValue.toLocaleString("en-IN")
                          : (actualValue ?? "-")
                      }
                    >
                      {val}
                      {rawVal?.isPercentage === "Yes" && "%"}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

RightBodyPane.propTypes = {
  styles: PropTypes.object,
  rightBodyRef: PropTypes.object,
  rightPaneWidth: PropTypes.string,
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  ROW_HEIGHT: PropTypes.number,
  totalRow: PropTypes.object,
  rows: PropTypes.array,
  scrollableGroups: PropTypes.array,
  getGroupObj: PropTypes.func,
  getChildVal: PropTypes.func,
  formatValue: PropTypes.func,
};
